import { Link, useParams } from 'react-router-dom'
import type { Kalla } from '../types'
import { hittaTeoripost, kallorFor, kategoriEtikett, TYP_ETIKETT } from '../lib/teorisektion'
import { useSidTitel } from '../lib/seo'

/** En källrad i postens källhänvisning och i samlade källförteckningen. */
export function KallRad({ kalla }: { kalla: Kalla }) {
  return (
    <>
      {kalla.forfattare && <>{kalla.forfattare}: </>}
      {kalla.url ? (
        <a href={kalla.url} target="_blank" rel="noopener">
          <em>{kalla.titel}</em>
        </a>
      ) : (
        <em>{kalla.titel}</em>
      )}
      {kalla.ar && <> ({kalla.ar})</>}
    </>
  )
}

/** Postvyn: en modell/teori som uppslagssida — figur, kärnpoäng, förklaring,
 *  ev. källkritik, "Läs mer i läromedlet"-länk till kapitelavsnittet och
 *  källhänvisning ur källregistret. */
export default function ModellSida() {
  const { teoriId } = useParams<{ teoriId: string }>()
  const post = teoriId ? hittaTeoripost(teoriId) : null
  useSidTitel(post?.namn)

  if (!post) {
    return (
      <div className="wrap">
        <div className="dokument">
          <h1>Posten finns inte</h1>
          <Link className="knapp knapp-primar" to="/modeller">
            Till modeller och teorier
          </Link>
        </div>
      </div>
    )
  }

  const kallor = kallorFor(post)
  const jamforbara = post.jamforbar_med.map((id) => hittaTeoripost(id)).filter((p) => p !== null)

  return (
    <div className="wrap">
      <article className="dokument teoripost">
        <nav className="brodsmula no-print" aria-label="Brödsmulor">
          <Link to="/modeller">Modeller och teorier</Link>
        </nav>

        <div className="teorikort-meta">
          <span className={`typ-badge typ-${post.typ}`}>{TYP_ETIKETT[post.typ]}</span>
          <span className="teorikort-kategori">{kategoriEtikett(post.kategori)}</span>
        </div>
        <h1>{post.namn}</h1>
        {post.upphov && <p className="teoripost-upphov">{post.upphov}</p>}

        <p className="teoripost-karnpoang">{post.karnpoang}</p>

        {post.illustration && (
          <span className="illustration">
            <img src={post.illustration.fil} alt={post.illustration.alt} />
          </span>
        )}

        <p className="teoripost-forklaring">{post.forklaring}</p>

        {post.kallkritik && (
          <aside className="teoripost-kallkritik" aria-label="Källkritiskt förbehåll">
            <strong>Källkritik:</strong> {post.kallkritik}
          </aside>
        )}

        {post.kapitel.length > 0 && (
          <>
            <h2>Läs mer i läromedlet</h2>
            <ul>
              {post.kapitel.map((ref) => (
                <li key={`${ref.slug}-${ref.avsnitt_rubrik}`}>
                  <Link to={`/kapitel/${ref.slug}${ref.ankare ? `#${ref.ankare}` : ''}`}>
                    Kapitel {ref.kapitel_id}: {ref.titel}
                  </Link>{' '}
                  — avsnittet ”{ref.avsnitt_rubrik}”
                </li>
              ))}
            </ul>
          </>
        )}

        {kallor.length > 0 && (
          <>
            <h2>Källa{kallor.length > 1 ? 'r' : ''}</h2>
            <ul className="teoripost-kallor">
              {kallor.map((kalla) => (
                <li key={kalla.kalla_id}>
                  <KallRad kalla={kalla} />
                </li>
              ))}
            </ul>
          </>
        )}

        {jamforbara.length > 0 && (
          <>
            <h2>Jämför med</h2>
            <div className="teoripost-jamfor no-print">
              {jamforbara.map((annan) => (
                <Link
                  key={annan.teori_id}
                  className="knapp knapp-sekundar knapp-liten"
                  to={`/modeller/jamfor?a=${post.teori_id}&b=${annan.teori_id}`}
                >
                  ⇄ {annan.namn}
                </Link>
              ))}
            </div>
          </>
        )}
      </article>
    </div>
  )
}
